import React, { useState, useEffect, useRef } from "react";
import { useApp } from "context";
import { Custom, Modal } from "components";
import { Enums, Constants, Helpers } from "utils";
import { ButtonGroup, Grid, IconButton } from "@mui/material";
import { PairValue } from "types";
import { IAppointment, IProvider, IService } from "interfaces";
import GetAppIcon from "@mui/icons-material/GetApp";
import EditIcon from "@mui/icons-material/Edit";

type Props = {
  opened?: boolean;
  toggle?: () => void;
};

export const QarePlan = (props: Props) => {
  const { t, theme, service: providedService, locale } = useApp();

  const [appointments, setAppointments] = useState<IAppointment[]>([]);
  const [services, setServices] = useState<PairValue[]>([]);
  const [serviceProviderMap, setServiceProviderMap] = useState<
    Map<number, PairValue[]>
  >(new Map());
  const [qarePlanModalOpened, setQarePlanModalOpened] = useState(false);
  const [appointment, setAppointment] = useState<IAppointment | null>(null);
  const [period, setPeriod] = useState<Enums.EnumPeriod>(Enums.EnumPeriod.Day);
  const [diagnoses, setDiagnoses] = useState<PairValue[]>([]);
  const [incrementComponent, setIncrementComponent] = useState(0);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const initialRender = useRef(true);
  const hiddenAnchor = useRef<HTMLAnchorElement | null>(null);

  useEffect(() => {
    findAndSetAppointments(period);
    if (initialRender.current) {
      findAndSetServicesAndProviders();
      findAndSetDiagnoses();
      initialRender.current = false;
    }
  }, [period, incrementComponent]);

  // TODO: there should be a route to do the filter and sort from DB for appointments. lazy programming.
  const findAndSetAppointments = (period = Enums.EnumPeriod.Day) => {
    providedService.appointment.period(period).then((response) => {
      setAppointments(
        response.appointments
          .filter((app: IAppointment) => app.checkInTime)
          .sort((a: IAppointment, b: IAppointment) =>
            b.checkInTime.localeCompare(a.checkInTime)
          )
      );
    });
  };

  const findAndSetServicesAndProviders = async () => {
    const providers = (await providedService.provider.all()).map(
      (item: IProvider) =>
        ({
          key: item.providerId,
          value: Helpers.replaceComma(item.username),
        } as PairValue)
    );

    providedService.service.all().then((response) => {
      const map = new Map();
      setServices(
        response.map((item: IService) => {
          map.set(
            item.serviceId,
            item.providerIds
              ?.split(",")
              .map((id) =>
                providers.find((p: PairValue) => p.key === Number(id))
              )
          );
          return {
            key: item.serviceId,
            value: item.serviceName,
          } as PairValue;
        })
      );
      setServiceProviderMap(map);
    });
  };

  const findAndSetDiagnoses = () =>
    providedService.diagnosis.all().then((response) => setDiagnoses(response));

  const action = (
    <ButtonGroup>
      {[
        { label: t.relative_date.today, period: Enums.EnumPeriod.Day },
        { label: t.relative_date.last_week, period: Enums.EnumPeriod.Week },
        { label: t.relative_date.last_month, period: Enums.EnumPeriod.Month },
      ].map((item, index) => (
        <Custom.Button
          key={index}
          size="small"
          variant="contained"
          onClick={() => setPeriod(item.period)}
          sx={{ marginBottom: theme.margin.sm }}
        >
          {item.label}
        </Custom.Button>
      ))}
    </ButtonGroup>
  );

  const columns = [
    {
      field: "username",
      width: 200,
      headerName: t.label.name,
      renderCell: ({ row }: { row: IAppointment }) =>
        Helpers.replaceComma(row.username),
    },
    {
      field: "checkInTime",
      width: 200,
      headerName: t.label.check_in_datetime,
      renderCell: ({ row }: { row: IAppointment }) =>
        new Date(row.checkInTime).toLocaleTimeString(
          locale,
          Constants.DATE_FORMAT
        ),
    },
    {
      field: "reason",
      width: 200,
      sortable: false,
      headerName: t.label.reason,
    },
    {
      field: "diagnosis",
      width: 200,
      sortable: false,
      headerName: t.label.diagnosis,
      valueGetter: ({ row }: { row: IAppointment }) =>
        diagnoses.find((d) => d.key === row.qarePlan?.diagnosisCodeId)?.value,
    },
    {
      field: "patientPlan",
      width: 200,
      sortable: false,
      headerName: t.label.patient_plan,
      valueGetter: ({ row }: { row: IAppointment }) => row.qarePlan?.content,
    },
    {
      field: "documents",
      width: 100,
      sortable: false,
      headerName: t.label.documents,
      renderCell: ({ row }: { row: IAppointment }) =>
        !!row.qarePlan?.documentNames.length && (
          <IconButton
            onClick={(e) => {
              onRowClick(row);
              setAnchorEl(e.currentTarget);
            }}
          >
            <GetAppIcon />
          </IconButton>
        ),
    },
    {
      field: "actions",
      width: 200,
      sortable: false,
      headerName: t.label.actions,
      renderCell: ({ row }: { row: IAppointment }) => (
        <IconButton onClick={() => onEditClick(row)}>
          <EditIcon />
        </IconButton>
      ),
    },
  ];

  const onRowClick = (row: IAppointment) => {
    setAppointment(
      appointments.find(
        (app: IAppointment) => app.appointmentId === row.appointmentId
      ) ?? null
    );
  };

  const onEditClick = (row: IAppointment) => {
    onRowClick(row);
    setQarePlanModalOpened(true);
  };

  const handleCloseQarePlanModal = () => {
    setQarePlanModalOpened(false);
    setAppointment(null);
    setIncrementComponent(incrementComponent + 1);
  };

  // TODO: the following function is almost repeated in QarePlanPatient component.
  // also, hiddenAnchor can be a global object for all downloads, same as hiddenInput for all file attachments.
  const handleDownloadDocument = async (
    appointmentId: number,
    documentName: string
  ) => {
    const [status, response] = await providedService.qarePlan.downloadDocument(
      appointmentId,
      documentName
    );

    if (status !== Enums.EnumResponse.Success) return;

    Helpers.download(
      response.data,
      documentName,
      hiddenAnchor.current as HTMLAnchorElement
    );
  };

  return (
    <Custom.Card
      title={t.title.myqare_plans}
      action={action}
      actionSx={{ float: "initial", marginTop: "1rem" }}
      opened={props.opened}
      toggle={props.toggle}
    >
      <a style={{ display: "none" }} ref={hiddenAnchor} />
      <Grid container spacing={theme.margin.md}>
        <Grid item xs={12} md={12}>
          <Custom.DataGrid
            pageSize={25}
            columns={columns}
            rows={appointments}
            isLoading={false}
            quickSearch
            getRowId={(row: IAppointment) => row.appointmentId}
          ></Custom.DataGrid>
          <Custom.Popover anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
            {appointment?.qarePlan?.documentNames.map((dn) => (
              <Custom.Chip
                label={dn}
                key={dn}
                onClick={() => {
                  handleDownloadDocument(
                    appointment.qarePlan?.appointmentId ?? NaN,
                    dn
                  );
                }}
              />
            ))}
          </Custom.Popover>
        </Grid>
      </Grid>
      <Modal.QarePlan
        appointment={appointment}
        services={services}
        serviceProviderMap={serviceProviderMap}
        diagnoses={diagnoses}
        opened={qarePlanModalOpened}
        onClose={handleCloseQarePlanModal}
      />
    </Custom.Card>
  );
};
